import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  DARK_MAP_STYLES_CONFIG,
  LIGHT_MAP_STYLES_CONFIG,
  vectorCustomizationAdapter,
} from "@locators/components/shared/store-locator.config";
import type { Feature } from "@yandex/ymaps3-clusterer";
import type { LngLat, YMap } from "@yandex/ymaps3-types";
import debounce from "lodash.debounce";

import { useConfig } from "@jti/app-context/useConfig";
import type { FlotiqStore } from "@jti/fe-common";
import { useMessageBus } from "@jti/fe-message-bus";
import { useDimension } from "@jti/ui/hooks/useDimension";

import { createStoreIconFactory } from "../../store-locator-icons";

import type { UseYandexMapsStoreLocator } from "./useYandexMapsStoreLocator.types";
import { StoreLocatorEvents } from "./useYandexMapsStoreLocator.types";
import {
  createClusterer,
  createSuggestionItems,
  DESKTOP_BREAKDOWN,
  getCustomMarker,
  getUserLocationMarker,
  getUserLocationPopup,
  mapStoresPoints,
  MARKER_SOURCE,
  updateDropdownContainer,
  updateStoreElements,
} from "./utils";

import styles from "./yandexStyles.module.scss";

type NoStoresSearchState = { value: boolean; type: string } | null;
type YandexLocation =
  { center: LngLat; zoom?: number; duration?: number } | { center?: LngLat; zoom: number; duration?: number };

export const useYandexMapsStoreLocator: UseYandexMapsStoreLocator = ({
  defaultLat = "0",
  defaultLng = "0",
  defaultZoom = 10,
  activeStoreId,
  handleSelectStore,
  handleLoadFlotiqStores,
  defaultStoreIcon,
  activeStoreIcon,
  storeIcons,
  isSearchVisible,
  onActivity,
  onTap,
  mapTheme,
}) => {
  const messageBus = useMessageBus();
  const { width } = useDimension();
  const isDesktopView = width > DESKTOP_BREAKDOWN;
  const {
    mapProviderExternalJs,
    storeLocatorMapTheme = "dark",
    storeLocatorLayoutVersion: layoutVersion,
  } = useConfig();
  const resolvedMapTheme = mapTheme ?? storeLocatorMapTheme;
  const inputRef = useRef<HTMLInputElement>(null);
  const yandexMapRef = useRef<YMap | null>(null);
  const mapRef = useRef<HTMLDivElement>(null);

  const [map, setMap] = useState<YMap | null>(null);
  const [isSearchResultPending, setIsSearchResultPending] = useState(false);
  const [markers, setMarkers] = useState<FlotiqStore[]>([]);
  const [clusters, setClusters] = useState<unknown | null>(null);
  const [isMapLoading, setIsMapLoading] = useState(false);
  const [isMapError, setIsMapError] = useState(false);
  const isInitializingRef = useRef(false);
  const isInitializedRef = useRef(false);
  const [isNoStoresSearch, setIsNoStoresSearch] = useState<NoStoresSearchState>(null);

  const iconFactory = useMemo(() => {
    const storeTypeOverrides = (storeIcons ?? []).reduce<Record<string, string>>((acc, { storeIcon, storeType }) => {
      if (storeType && storeIcon) {
        acc[storeType] = storeIcon;
      }
      return acc;
    }, {});

    const storeIconFactory = createStoreIconFactory({
      layoutVersion,
      theme: resolvedMapTheme,
      defaultStoreIcon,
      activeStoreIcon,
      storeTypeOverrides,
    });

    return (store: FlotiqStore) => storeIconFactory(store) ?? { default: "" };
  }, [layoutVersion, resolvedMapTheme, storeIcons, defaultStoreIcon, activeStoreIcon]);

  const handleLoadMarkers = async (stores: FlotiqStore[]) => {
    if (!yandexMapRef.current) {
      return;
    }
    const currentMap = yandexMapRef.current;

    if (stores.length > 0) {
      setIsNoStoresSearch(null);
    }

    const { YMapMarker } = ymaps3;
    setMarkers(stores);

    let clusterer;
    const points = mapStoresPoints(stores);
    const createMarker = (feature: Feature) => {
      const {
        id,
        geometry: { coordinates },
        properties,
      } = feature;

      const store = properties?.store as FlotiqStore | undefined;
      const markerElement = store ? getCustomMarker(store, iconFactory) : document.createElement("div");

      const newMarker = new YMapMarker(
        { coordinates: coordinates, id: id, source: MARKER_SOURCE, zIndex: 1 },
        markerElement
      );

      return newMarker;
    };

    const getClustererCircle = createClusterer({ layoutVersion });

    const createCluster = (coordinates: LngLat, features: Feature[]) =>
      new YMapMarker({ coordinates }, getClustererCircle(features.length).cloneNode(true) as HTMLElement);

    if (clusters) {
      const clustererLayer = currentMap.children.find(
        (clusterer) =>
          "_props" in clusterer &&
          Boolean((clusterer as unknown as { _props?: { features?: Feature[] } })._props?.features)
      );
      clustererLayer?.update({ features: points });
    } else {
      addZoomButtons();
      const { YMapClusterer, clusterByGrid } = await ymaps3.import("@yandex/ymaps3-clusterer@0.0.1");
      clusterer = new YMapClusterer({
        method: clusterByGrid({ gridSize: 64 }),
        features: points,
        marker: createMarker,
        cluster: createCluster,
      });

      setClusters(clusterer);
      currentMap.addChild(clusterer);
    }
  };

  const handleCreateMap = useCallback(async () => {
    await ymaps3.ready;

    const { YMap, YMapDefaultSchemeLayer, YMapDefaultFeaturesLayer, YMapListener, YMapLayer, YMapFeatureDataSource } =
      ymaps3;

    const customization = vectorCustomizationAdapter(
      resolvedMapTheme === "light" ? LIGHT_MAP_STYLES_CONFIG : DARK_MAP_STYLES_CONFIG
    );

    if (!mapRef.current) {
      return;
    }

    const newMap = new YMap(
      mapRef.current,
      {
        location: {
          center: [Number(defaultLng), Number(defaultLat)],
          zoom: defaultZoom,
        },
        theme: resolvedMapTheme,
      },
      [
        new YMapDefaultSchemeLayer({
          customization,
        }),
        new YMapDefaultFeaturesLayer({}),
        new YMapFeatureDataSource({ id: MARKER_SOURCE }),
        new YMapLayer({ source: MARKER_SOURCE, type: "markers" }),
        new YMapListener({
          onActionStart: onActivity,
          onClick: clickHandler,
          onActionEnd: (object) => {
            if (object.type === "scrollZoom") {
              updateStoreElements(MARKER_SOURCE, iconFactory);
              handleSelectStore("");
            }
            const debouncedLoadFlotiqStores = debounce(handleLoadFlotiqStores);
            debouncedLoadFlotiqStores(yandexMapRef.current);
          },
        }),
      ]
    );

    setMap(newMap);
    yandexMapRef.current = newMap;
    handleSelectStore("");
    handleLoadFlotiqStores(yandexMapRef.current);
    inputRef.current?.addEventListener?.("input", handleAutoComplete);
  }, [defaultLat, defaultLng, defaultZoom, handleLoadFlotiqStores, handleSelectStore, iconFactory, resolvedMapTheme]);

  const clickHandler = (object: unknown) => {
    const eventObject = object as { entity?: { _props?: { id?: string } } } | undefined;
    handleSelectStore(eventObject?.entity?._props?.id ?? "");
    onTap?.();
  };

  const updateMapLocation = (location: YandexLocation, yandexMap?: YMap | null) => {
    (map || yandexMap)?.update({
      location: {
        ...location,
        duration: 400,
      },
    });
  };

  const handleInitMap = useCallback(() => {
    if (isInitializingRef.current || isInitializedRef.current) {
      return;
    }

    if (!mapProviderExternalJs) {
      setIsMapError(true);
      setIsMapLoading(false);
      return;
    }

    const existingScript = document.querySelector(`script[src="${mapProviderExternalJs}"]`);
    if (existingScript) {
      handleCreateMap();
      return;
    }

    isInitializingRef.current = true;
    setIsMapLoading(true);

    const head = document.head;
    const script = document.createElement("script");
    script.src = mapProviderExternalJs;
    script.type = "text/javascript";

    script.onload = async () => {
      try {
        await handleCreateMap();
        isInitializedRef.current = true;
      } finally {
        isInitializingRef.current = false;
        setIsMapLoading(false);
      }
    };

    script.onerror = () => {
      isInitializingRef.current = false;
      setIsMapError(true);
      setIsMapLoading(false);
    };

    if (!existingScript) {
      head.appendChild(script);
    }
  }, [handleCreateMap, mapProviderExternalJs]);

  const handleLoadUserMarker = (userLat: number, userLng: number) => {
    if (!map) {
      return;
    }

    const { YMapMarker } = ymaps3;
    const coordinates = [userLng, userLat] as LngLat;
    const marker = new YMapMarker({ coordinates: coordinates }, getUserLocationMarker());

    if (isDesktopView) {
      const popup = new YMapMarker({ coordinates: coordinates }, getUserLocationPopup());
      map.addChild(marker).addChild(popup);
    } else {
      map.addChild(marker);
    }

    setIsNoStoresSearch({ value: true, type: "location" });
    updateMapLocation({ center: coordinates });
  };

  const handleInputSearchClick = async () => {
    setIsSearchResultPending(true);

    if (inputRef.current) {
      const searchValue = inputRef.current.value;
      messageBus.publish("tracking.event", {
        payload: {
          event_name: StoreLocatorEvents.SearchClick,
          additionalData: {
            search_term: searchValue,
          },
        },
      });

      if (searchValue) {
        const searchResponse = await ymaps3.search({ text: searchValue });
        const geometry = searchResponse[0]?.geometry;
        if (!geometry) {
          setIsSearchResultPending(false);
          return;
        }

        handleLoadFlotiqStores(yandexMapRef.current);
        updateMapLocation({ center: geometry.coordinates, zoom: 15 }, yandexMapRef.current);
        setIsNoStoresSearch({ value: true, type: "search" });
      }
    }
    setIsSearchResultPending(false);
  };

  const handleAutoComplete = async () => {
    const searchValue = inputRef.current?.value;

    if (searchValue && yandexMapRef.current) {
      const { bounds, center } = yandexMapRef.current;
      const suggestions = await ymaps3.suggest({
        text: searchValue,
        limit: 5,
        bounds: bounds,
        center: center as LngLat,
      });
      if (suggestions) {
        createSuggestionItems(inputRef, suggestions, handleInputSearchClick);
      }
    }
  };

  useEffect(() => {
    if (!map && !isInitializingRef.current) {
      handleInitMap();
    }
  }, [map]);

  useEffect(() => {
    const defaultZoom = 15;
    if (activeStoreId) {
      const activeMarker = markers.find((marker) => marker.id === activeStoreId);
      handleLoadFlotiqStores(yandexMapRef.current);
      const currentZoom = yandexMapRef.current?.zoom;
      const zoom = currentZoom && currentZoom > defaultZoom ? currentZoom : defaultZoom;
      if (activeMarker) {
        updateMapLocation({
          center: [activeMarker.Location.lon, activeMarker.Location.lat],
          zoom,
        });
      }
      setTimeout(() => updateStoreElements(MARKER_SOURCE, iconFactory, activeStoreId), 500);
    }
  }, [activeStoreId, iconFactory]);

  const handleClickOutside = (event: MouseEvent) => {
    const target = event.target;
    if (
      inputRef.current &&
      target instanceof HTMLElement &&
      !(inputRef.current.contains(target) || target.classList.contains(`${styles.yandexDropdownItem}`))
    ) {
      updateDropdownContainer();
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [inputRef]);

  const addZoomButtons = () => {
    if (!map) {
      return;
    }

    const { YMapControls, YMapControlButton } = ymaps3;

    const plusSign = document.createElement("div");
    plusSign.innerHTML = "+";
    const zoomPlusButton = new YMapControlButton({
      element: plusSign,
      onClick: () => {
        handleLoadFlotiqStores(yandexMapRef.current);
        updateMapLocation({ zoom: map.zoom + 1 });
      },
    });
    const minusSign = document.createElement("div");
    minusSign.innerHTML = "&minus;";
    const zoomMinusButton = new YMapControlButton({
      element: minusSign,
      onClick: () => {
        handleLoadFlotiqStores(yandexMapRef.current);
        updateMapLocation({ zoom: map.zoom - 1 });
      },
    });

    map?.addChild(new YMapControls({ position: "left" }, [zoomPlusButton, zoomMinusButton]));
  };

  const handleMapExpansion = () => {
    const currentZoom = yandexMapRef.current?.zoom;
    const mapExpansionZoom = 7;
    const zoom =
      currentZoom && currentZoom > mapExpansionZoom ? mapExpansionZoom : (currentZoom ?? mapExpansionZoom) - 1;
    updateMapLocation({ zoom });
  };

  return {
    mapRef,
    inputRef,
    isMapLoading,
    isMapError,
    isSearchResultPending,
    handleInitMap,
    handleInputSearchClick,
    handleLoadMarkers,
    handleLoadUserMarker,
    isSearchVisible,
    isNoStoresSearch,
    handleMapExpansion,
  };
};
